/**
 * Encodage et comptage d'un SMS — la seule vérité sur « ce qui tient en 160 ».
 *
 * Partagé serveur ↔ éditeur du back-office : le compteur affiché sous le
 * textarea et la validation bloquante de l'API doivent donner le même nombre,
 * sinon un template validé à l'écran est refusé à l'enregistrement.
 *
 * Le piège qu'on ferme ici : **un seul** caractère hors GSM 03.38 fait basculer
 * tout le message en UCS-2, où la limite tombe de 160 à 70. Les accents français
 * (`é è à ù É Ç`) appartiennent au jeu GSM-7 et ne coûtent rien ; ce sont les
 * caractères qu'un traitement de texte substitue en silence (apostrophe
 * typographique, points de suspension, tirets longs, espace insécable) qui font
 * partir un message de 146 caractères en trois segments facturés trois crédits.
 */
/**
 * Jeu de base GSM 03.38, dans l'ordre des positions 0x00 à 0x7F. Chaque
 * caractère y coûte un septet.
 */
const GSM7_BASIC_CHARS = '@£$¥èéùìòÇ\nØø\rÅå' +
    'Δ_ΦΓΛΩΠΨΣΘΞÆæßÉ' +
    ' !"#¤%&\'()*+,-./' +
    '0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNO' +
    'PQRSTUVWXYZÄÖÑÜ§' +
    '¿abcdefghijklmno' +
    'pqrstuvwxyzäöñüà';
/**
 * Table d'extension : ces caractères existent en GSM-7 mais coûtent **deux**
 * septets (un échappement + le caractère). Un texte chargé d'accolades ou
 * d'euros consomme donc son budget deux fois plus vite.
 */
const GSM7_EXTENDED_CHARS = '\f^{}\\[~]|€';
const GSM7_BASIC = new Set(GSM7_BASIC_CHARS);
const GSM7_EXTENDED = new Set(GSM7_EXTENDED_CHARS);
/** Capacité d'un SMS GSM-7 seul, puis d'un segment concaténé (6 septets d'en-tête). */
const GSM7_SINGLE_LIMIT = 160;
const GSM7_MULTIPART_LIMIT = 153;
/** Idem en UCS-2, où l'unité est l'unité UTF-16 et non le septet. */
const UCS2_SINGLE_LIMIT = 70;
const UCS2_MULTIPART_LIMIT = 67;
/**
 * Longueur maximale d'un SMS de la maison : un seul segment, jamais deux.
 * Validation bloquante côté serveur, mesurée sur l'aperçu (mention STOP
 * comprise) et non sur le corps saisi.
 */
export const SMS_MAX_CHARS = GSM7_SINGLE_LIMIT;
/**
 * Substitutions appliquées **avant** de compter et d'envoyer.
 *
 * Toutes ont la même origine : la correction automatique d'un traitement de
 * texte, invisible à l'œil dans un textarea. Le remplacement est volontairement
 * conservateur — il ne touche qu'à des caractères dont l'équivalent ASCII est
 * incontestable, jamais aux accents, qui sont légitimes en GSM-7.
 */
const NORMALIZATIONS = [
    // Fin de ligne Windows : le CR coûte un septet pour rien. En premier, pour que
    // les règles suivantes travaillent sur un texte à sauts de ligne unifiés.
    [/\r\n/g, '\n'],
    // Apostrophes et quotes typographiques (Word, Docs, correction macOS).
    [/[‘’‚‛′]/g, "'"],
    // Guillemets : doubles courbes, bas-de-casse allemands, chevrons français.
    [/[“”„‟″«»]/g, '"'],
    // Points de suspension en un seul caractère.
    [/…/g, '...'],
    // Tirets : conditionnel, insécable, numérique, demi-cadratin, cadratin, barre.
    [/[‐‑‒–—―]/g, '-'],
    // Espaces exotiques : insécable, fine, cadratin, insécable étroite, idéographique.
    [/[  -   　]/g, ' '],
    // Espace de largeur nulle : invisible, et pourtant facturé.
    [/[​﻿]/g, ''],
    // Ligatures, absentes du jeu GSM (æ/Æ y sont, œ/Œ non — on traite les quatre
    // pareil pour que l'agent lise partout la même graphie).
    [/œ/g, 'oe'],
    [/Œ/g, 'OE'],
    // Puces et signes collés depuis une liste ou un logo.
    [/[•·]/g, '-'],
    [/™/g, 'TM'],
];
/**
 * Longueur, encodage et nombre de segments d'un texte **déjà normalisé**.
 *
 * Le texte est pris tel quel : c'est à l'appelant d'avoir passé
 * {@link normalizeSmsText} avant, pour que le compte affiché soit celui du
 * message réellement émis.
 */
export function countSmsCharacters(text) {
    const nonGsmChars = nonGsm7Characters(text);
    if (nonGsmChars.length > 0) {
        // UCS-2 : l'unité facturée est l'unité UTF-16, donc un emoji hors du plan
        // multilingue de base en coûte deux. `text.length` les compte déjà ainsi.
        const charCount = text.length;
        return {
            charCount,
            encoding: 'UCS2',
            nonGsmChars,
            segments: segmentsFor(charCount, UCS2_SINGLE_LIMIT, UCS2_MULTIPART_LIMIT),
        };
    }
    // `for…of` parcourt les points de code, et non les unités UTF-16 : c'est la
    // bonne granularité ici, le jeu GSM tenant entièrement dans le plan de base.
    let charCount = 0;
    for (const char of text) {
        charCount += GSM7_EXTENDED.has(char) ? 2 : 1;
    }
    return {
        charCount,
        encoding: 'GSM7',
        nonGsmChars,
        segments: segmentsFor(charCount, GSM7_SINGLE_LIMIT, GSM7_MULTIPART_LIMIT),
    };
}
/** `true` si chaque caractère du texte appartient au jeu GSM 03.38 (base ou extension). */
export function isGsm7(text) {
    return nonGsm7Characters(text).length === 0;
}
/**
 * Caractères qui font basculer le message en UCS-2, dédoublonnés et dans l'ordre
 * d'apparition — l'éditeur les surligne un par un ; « le message contient un
 * caractère spécial » n'aide personne à le retrouver dans 150 caractères.
 */
export function nonGsm7Characters(text) {
    const found = [];
    const seen = new Set();
    for (const char of text) {
        if (GSM7_BASIC.has(char) || GSM7_EXTENDED.has(char))
            continue;
        if (seen.has(char))
            continue;
        seen.add(char);
        found.push(char);
    }
    return found;
}
/**
 * Nettoie un corps de SMS de ce qu'un traitement de texte y a glissé.
 *
 * Idempotente : la rejouer sur un texte déjà normalisé ne change rien, ce qui
 * permet de l'appliquer à la saisie comme à l'envoi sans se demander si elle a
 * déjà tourné.
 */
export function normalizeSmsText(raw) {
    let text = raw;
    for (const [pattern, replacement] of NORMALIZATIONS) {
        text = text.replaceAll(pattern, replacement);
    }
    return text;
}
/**
 * Un message vide part quand même en un segment : c'est ainsi qu'il est
 * facturé, ce n'est pas une convention d'affichage.
 */
function segmentsFor(charCount, singleLimit, multipartLimit) {
    if (charCount <= singleLimit)
        return 1;
    return Math.ceil(charCount / multipartLimit);
}
